"use client";

import "@/components/bloks/defineBloks";
import { env } from "@/env.mjs";
import { CentraProvider, useLoggedIn } from "@frend-digital/centra/client";
import { StoryblokProvider } from "@frend-digital/storyblok";
import { UIProvider } from "@frend-digital/ui";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { SegmentControlProvider } from "@/components/SegmentControl/SegmentControl";
import { ConfirmRoot } from "@/components/ui/Confirm";
import { URLDiscount } from "@/components/ui/URLDiscount";
import { LoyaltyScripts } from "@/features/loyalty/LoyaltyScripts";
import { useFlag } from "@/lib/flags/client";
import { parseGeo } from "@frend-digital/next/i18n";
import { Result } from "@frend-digital/utils/results";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider } from "jotai";
import dynamic from "next/dynamic";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import type React from "react";
import { useEffect, useState } from "react";

const Events = dynamic(() => import("./Events"));

interface Props {
	children: React.ReactNode;
}

export const Providers = ({ children }: Props) => {
	const router = useRouter();
	const [queryClient] = useState(() => new QueryClient());
	const { geo } = useParams<{ geo: string }>();

	const geoResult = Result.unwrapOr(parseGeo(geo), null);
	const loyalty = useFlag("loyalty.enabled");

	const handlePublish = () => {
		router.refresh();
	};

	return (
		<UIProvider
			value={{
				navigation: {
					back: router.back.bind(router),
					push: router.push.bind(router),
					replace: router.replace.bind(router),
					useSearchParams,
				},
			}}
		>
			<QueryClientProvider client={queryClient}>
				<StoryblokProvider onPublished={handlePublish}>
					<CentraProvider apiUrl={env.NEXT_PUBLIC_CENTRA_CHECKOUT}>
						<Provider>
							<SegmentedProviders country={geoResult?.country} loyalty={loyalty}>
								{children}
							</SegmentedProviders>
							{loyalty && <LoyaltyScripts />}
							<ConfirmRoot />
						</Provider>
						<Events />
						<URLDiscount />
					</CentraProvider>
				</StoryblokProvider>
				<ReactQueryDevtools buttonPosition="bottom-right" />
			</QueryClientProvider>
		</UIProvider>
	);
};

// useLoggedIn() needs a CentraProvider ancestor, which Providers itself
// renders rather than sits inside of - so the segment values are assembled
// here, one level further down.
const SegmentedProviders = ({
	children,
	country,
	loyalty,
}: Props & { country: string | undefined; loyalty: boolean }) => {
	const { data: loggedIn } = useLoggedIn();

	return (
		<SegmentControlProvider
			segments={{
				countries: country,
				require_loyalty: loyalty,
				loggedIn,
			}}
		>
			{children}
		</SegmentControlProvider>
	);
};
